import {ActionButton, Label, Pivot, PivotItem, Spinner, Stack, Text, ICommandBarItemProps,} from "@fluentui/react";
import {SharedColors} from "@fluentui/theme";
import Axios from "axios";
import {FC, useCallback, useEffect, useMemo, useRef, useState} from "react";
import {Controlled as CodeMirror} from "react-codemirror2";
import {useTranslation} from "react-i18next";
import {useBoolean} from "@fluentui/react-hooks";
import {CMEditor, SQLDefinition, SupportCodeLanguage} from "./CMElement";
import {MakeSQLResponseTable} from "../helpers/MakeSQLResponseTable";
import {TenantList} from "./TenantList";
import {TenantResource} from "../enums/TenantResource";
import {
    SqlObjectDefinitionDto,
    SqlObjectDefinitionRequestDto,
    SqlObjectMetadataDto,
    SqlRoutineMetadataDto,
    SqlSessionInfoDto,
    SqlTableMetadataDto,
    SqlTemplateDto,
    SqlUserInfoDto,
} from "../API";
import {
    CheckboxElement,
    CMEditorElement,
    ITenantColumn,
    ITenantFormLayout,
    MultilineTextElement,
    TextFieldElement,
} from "./OnixElements";
import {MakeTenantColumns} from "../helpers/MakeTenantColumns";
import {FormAction, TWithStoragesAndOriginals} from "../types/TenantTypes";
import {AlertApiError} from "./ShowApiError";
import {SqlObjectDetailsPane, SqlObjectParentIdentity} from "./SqlObjectDetailsPane";
import {
    getSqlFunctionMetadataKey,
    getSqlObjectMetadataKey,
    getSqlProcedureMetadataKey,
    getSqlTableMetadataKey,
    getSqlUserMetadataKey,
    getSqlViewMetadataKey,
} from "./SqlObjectMetadataKeys";

interface ISQLEditorGlobal {
    setSqlQuery: (sqlQuery: string) => void;
    mustCheck: boolean;
    loading: boolean;
    sqlQueryResponse: any[][] | Record<string, any[][]> | undefined;
    executeSqlQuery: (e: any, query: string | undefined) => void;
    sqlQueryError: string | undefined;
    sqlQueryWarning: string | undefined;
    sqlQuery: string | null | undefined;
    sqlQueryChanged: (event: any, newValue: string | undefined) => void;
    identifier: string;
    getSQLDefinitions?: () => Promise<SQLDefinition[]>;
    isMultiCompany?: boolean;
    defaultHeight?: any;
    inline?: boolean;
    metadataCompanyId?: number;
    metadataConnectionString?: string;
    additionalCommands?: ICommandBarItemProps[];
}

export const SQLEditorGlobal: FC<ISQLEditorGlobal> = ({
                                                          setSqlQuery,
                                                          mustCheck,
                                                          loading,
                                                          sqlQueryResponse,
                                                          executeSqlQuery,
                                                          sqlQueryError,
                                                          sqlQueryWarning,
                                                          inline,
                                                          sqlQuery,
                                                          sqlQueryChanged,
                                                          defaultHeight,
                                                          identifier,
                                                          getSQLDefinitions,
                                                          isMultiCompany,
                                                          metadataCompanyId,
                                                          metadataConnectionString,
                                                          additionalCommands,
                                                      }) => {
    const {t} = useTranslation();
    const [loadingLocal, {setTrue: setLoadingTrue, setFalse: setLoadingFalse}] =
        useBoolean(false);
    const [localSqlQuery, setLocalSqlQuery] = useState<string | null>(
        sqlQuery ?? "",
    );
    const syncTokenRef = useRef<ReturnType<typeof setTimeout> | undefined>();

    useEffect(() => {
        setLocalSqlQuery(sqlQuery ?? "");
    }, [sqlQuery]);

    useEffect(() => {
        if (syncTokenRef.current) {
            clearTimeout(syncTokenRef.current);
        }
        syncTokenRef.current = setTimeout(() => {
            const value = localSqlQuery ?? "";
            setSqlQuery(value);
            sqlQueryChanged(undefined, value);
        }, 250);
        return () => {
            if (syncTokenRef.current) {
                clearTimeout(syncTokenRef.current);
            }
        };
    }, [localSqlQuery, setSqlQuery, sqlQueryChanged]);

    const metadataAvailable = Boolean(metadataCompanyId || metadataConnectionString);

    const formatDate = useCallback((value?: Date | string) => {
        if (!value) {
            return "";
        }

        const date = value instanceof Date ? value : new Date(value);
        if (Number.isNaN(date.getTime())) {
            return "";
        }

        return date.toLocaleString();
    }, []);

    const templateBaseDto =
        useMemo<TWithStoragesAndOriginals<Partial<SqlTemplateDto>>>(() => ({} as TWithStoragesAndOriginals<Partial<SqlTemplateDto>>), []);

    const makeResultData = useCallback(
        (sqlQueryResponseLocal: any[][] | Record<string, any[][]> | undefined) =>
            MakeSQLResponseTable({sqlQueryResponse: sqlQueryResponseLocal, inline, isMultiCompany}),
        [inline, isMultiCompany],
    );

    const makeResponseText = useCallback(
        (sqlQueryResponseLocal: any[][] | Record<string, any[][]> | undefined) => {
            if (!sqlQueryResponseLocal) {
                return undefined;
            }

            const flattenRows = (rows: any[][]) =>
                rows.reduce(
                    (previousValue, currentValue) =>
                        previousValue +
                        currentValue.reduce(
                            (previousValue1, currentValue1) =>
                                previousValue1 +
                                Object.keys(currentValue1).reduce(
                                    (previousValue2, currentValue2) =>
                                        previousValue2 + currentValue1[currentValue2],
                                    "",
                                ),
                            "",
                        ),
                    "",
                );

            if (Array.isArray(sqlQueryResponseLocal)) {
                return flattenRows(sqlQueryResponseLocal);
            }

            return Object.values(sqlQueryResponseLocal).reduce(
                (prev, curr) => prev + flattenRows(curr),
                "",
            );
        },
        [],
    );
    
    const templateColumns = useMemo<ITenantColumn<SqlTemplateDto>[]>(
        () =>
            MakeTenantColumns<SqlTemplateDto>(
                {
                    name: {
                        minWidth: 220,
                        translation: "sql_template_name",
                    },
                    category: {
                        minWidth: 140,
                        translation: "sql_template_category",
                    },
                    description: {
                        minWidth: 220,
                        translation: "sql_template_description",
                    },
                    isPinned: {
                        minWidth: 80,
                        translation: "sql_template_pinned",
                        render: (item) => <span>{(item.isPinned ? t("yes") : "")}</span>,
                    },
                    createdOn: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createdOn)}</span>,
                    },
                    modifiedOn: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifiedOn)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const templateForm = useMemo<ITenantFormLayout<SqlTemplateDto>>(
        () => ({
            name: {
                type: new TextFieldElement({}),
            },
            category: {
                type: new TextFieldElement({}),
            },
            description: {
                type: new MultilineTextElement({
                    rows: 3,
                }),
            },
            sqlText: {
                type: new CMEditorElement({
                    codeLanguage: "sql",
                    minHeight: 220,
                }),
            },
            isPinned: {
                type: new CheckboxElement({}),
            },
        }),
        [],
    );

    const setEditorValue = useCallback(
        (value: string) => {
            setLocalSqlQuery(value);
            setSqlQuery(value);
            sqlQueryChanged(undefined, value);
        },
        [setSqlQuery, sqlQueryChanged],
    );

    const handleTemplateApply = useCallback(
        (template: SqlTemplateDto) => {
            const value = template?.sqlText ?? "";
            setEditorValue(value);
        },
        [setEditorValue],
    );

    const loadMetadataDefinition = useCallback(
        async (request: SqlObjectDefinitionRequestDto) => {
            const {data} = await Axios.post<SqlObjectDefinitionDto>(
                "SqlMetadata/Definition",
                request,
                {
                    params: {
                        additionalIntParameter: metadataCompanyId,
                        additionalStringParameter: metadataConnectionString,
                    },
                },
            );
            setEditorValue(data.definition ?? "");
        },
        [metadataCompanyId, metadataConnectionString, setEditorValue],
    );

    const requestMetadataDefinition = useCallback(
        (request: SqlObjectDefinitionRequestDto) => {
            loadMetadataDefinition(request).catch(AlertApiError);
        },
        [loadMetadataDefinition],
    );

    const handleTableInvoked = useCallback(
        (item: SqlTableMetadataDto) =>
            requestMetadataDefinition({
                objectType: "table",
                schemaName: item.schemaName,
                objectName: item.tableName,
                parentSchemaName: "",
                parentObjectName: "",
            }),
        [requestMetadataDefinition],
    );

    const handleViewInvoked = useCallback(
        (item: SqlTableMetadataDto) =>
            requestMetadataDefinition({
                objectType: "view",
                schemaName: item.schemaName,
                objectName: item.tableName,
                parentSchemaName: "",
                parentObjectName: "",
            }),
        [requestMetadataDefinition],
    );

    const handleProcedureInvoked = useCallback(
        (item: SqlRoutineMetadataDto) =>
            requestMetadataDefinition({
                objectType: "procedure",
                schemaName: item.schemaName,
                objectName: item.routineName,
                parentSchemaName: "",
                parentObjectName: "",
            }),
        [requestMetadataDefinition],
    );

    const handleFunctionInvoked = useCallback(
        (item: SqlRoutineMetadataDto) =>
            requestMetadataDefinition({
                objectType: "function",
                schemaName: item.schemaName,
                objectName: item.routineName,
                parentSchemaName: "",
                parentObjectName: "",
            }),
        [requestMetadataDefinition],
    );

    const handleUserInvoked = useCallback(
        (item: SqlUserInfoDto) =>
            requestMetadataDefinition({
                objectType: "user",
                schemaName: "",
                objectName: item.userName,
                parentSchemaName: "",
                parentObjectName: "",
            }),
        [requestMetadataDefinition],
    );

    const handleSessionInvoked = useCallback(
        (item: SqlSessionInfoDto) =>
            requestMetadataDefinition({
                objectType: "session",
                schemaName: "",
                objectName: item.sessionId?.toString() ?? "",
                parentSchemaName: "",
                parentObjectName: "",
                sessionId: item.sessionId,
            }),
        [requestMetadataDefinition],
    );

    const handleObjectInvoked = useCallback(
        (item: SqlObjectMetadataDto) => requestMetadataDefinition({
            objectType: item.objectType,
            schemaName: item.schemaName,
            objectName: item.objectName,
            parentSchemaName: item.parentSchemaName,
            parentObjectName: item.parentObjectName,
        }),
        [requestMetadataDefinition],
    );

    const templateSelectionCommands = useCallback(
        (template: SqlTemplateDto) => [
            <ActionButton
                key="apply-template"
                iconProps={{iconName: "InsertTextBox"}}
                onClick={() => handleTemplateApply(template)}
            >
                {t("sql_apply_template")}
            </ActionButton>,
        ],
        [handleTemplateApply, t],
    );

    const tableColumns = useMemo<ITenantColumn<SqlTableMetadataDto>[]>(
        () =>
            MakeTenantColumns<SqlTableMetadataDto>(
                {
                    schemaName: {
                        minWidth: 140,
                        translation: "schema_name",
                    },
                    tableName: {
                        minWidth: 220,
                        translation: "table_name",
                    },
                    rowCount: {
                        minWidth: 120,
                        translation: "row_count",
                        render: (item) =>
                            <span>{item.rowCount !== undefined
                                ? item.rowCount.toLocaleString()
                                : ""}</span>,
                    },
                    createDate: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createDate)}</span>,
                    },
                    modifyDate: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifyDate)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const viewColumns = useMemo<ITenantColumn<SqlTableMetadataDto>[]>(
        () =>
            MakeTenantColumns<SqlTableMetadataDto>(
                {
                    schemaName: {
                        minWidth: 140,
                        translation: "schema_name",
                    },
                    tableName: {
                        minWidth: 220,
                        translation: "view_name",
                    },
                    createDate: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createDate)}</span>,
                    },
                    modifyDate: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifyDate)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const routineColumns = useMemo<ITenantColumn<SqlRoutineMetadataDto>[]>(
        () =>
            MakeTenantColumns<SqlRoutineMetadataDto>(
                {
                    schemaName: {
                        minWidth: 140,
                        translation: "schema_name",
                    },
                    routineName: {
                        minWidth: 220,
                        translation: "routine_name",
                    },
                    routineType: {
                        minWidth: 160,
                        translation: "routine_type",
                    },
                    createDate: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createDate)}</span>,
                    },
                    modifyDate: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifyDate)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const userColumns = useMemo<ITenantColumn<SqlUserInfoDto>[]>(
        () =>
            MakeTenantColumns<SqlUserInfoDto>(
                {
                    userName: {
                        minWidth: 200,
                        translation: "user_name",
                    },
                    type: {
                        minWidth: 150,
                        translation: "user_type",
                    },
                    authenticationType: {
                        minWidth: 180,
                        translation: "authentication_type",
                    },
                    defaultSchema: {
                        minWidth: 160,
                        translation: "default_schema",
                    },
                    createdOn: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createdOn)}</span>,
                    },
                    modifiedOn: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifiedOn)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const sessionColumns = useMemo<ITenantColumn<SqlSessionInfoDto>[]>(
        () =>
            MakeTenantColumns<SqlSessionInfoDto>(
                {
                    sessionId: {
                        minWidth: 100,
                        translation: "session_id",
                    },
                    loginName: {
                        minWidth: 180,
                        translation: "login_name",
                    },
                    hostName: {
                        minWidth: 150,
                        translation: "host_name",
                    },
                    programName: {
                        minWidth: 180,
                        translation: "program_name",
                    },
                    status: {
                        minWidth: 120,
                        translation: "status",
                    },
                    command: {
                        minWidth: 140,
                        translation: "command",
                    },
                    loginTime: {
                        minWidth: 160,
                        translation: "login_time",
                        render: (item) => <span>{formatDate(item.loginTime)}</span>,
                    },
                    cpuTimeMs: {
                        minWidth: 140,
                        translation: "cpu_time_ms",
                        render: (item) =>
                            <span>{item.cpuTimeMs !== undefined
                                ? item.cpuTimeMs
                                : ""}</span>,
                    },
                    totalElapsedTimeMs: {
                        minWidth: 170,
                        translation: "total_elapsed_time_ms",
                        render: (item) =>
                            <span>{item.totalElapsedTimeMs !== undefined
                                ? item.totalElapsedTimeMs
                                : ""}</span>,
                    },
                    blockingSessionId: {
                        minWidth: 150,
                        translation: "blocking_session_id",
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const objectColumns = useMemo<ITenantColumn<SqlObjectMetadataDto>[]>(
        () =>
            MakeTenantColumns<SqlObjectMetadataDto>(
                {
                    schemaName: {
                        minWidth: 130,
                        translation: "schema_name",
                    },
                    objectName: {
                        minWidth: 200,
                        translation: "sql_object",
                    },
                    parentObjectName: {
                        minWidth: 180,
                        translation: "sql_parent",
                    },
                    objectKind: {
                        minWidth: 150,
                        translation: "sql_object_type",
                    },
                    details: {
                        minWidth: 220,
                        translation: "sql_details",
                    },
                    isDisabled: {
                        minWidth: 100,
                        translation: "sql_disabled",
                        render: (item) => <span>{item.isDisabled ? t("yes") : ""}</span>,
                    },
                    createDate: {
                        minWidth: 160,
                        translation: "created_on",
                        render: (item) => <span>{formatDate(item.createDate)}</span>,
                    },
                    modifyDate: {
                        minWidth: 160,
                        translation: "modified_on",
                        render: (item) => <span>{formatDate(item.modifyDate)}</span>,
                    },
                },
                t,
            ),
        [t, formatDate],
    );

    const renderObjectDetails = useCallback(
        (parent?: SqlObjectParentIdentity) => <SqlObjectDetailsPane
            parent={parent}
            metadataCompanyId={metadataCompanyId}
            metadataConnectionString={metadataConnectionString}
            onDefinitionRequested={requestMetadataDefinition}
        />,
        [metadataCompanyId, metadataConnectionString, requestMetadataDefinition],
    );

    const metadataReadOnlyActions = useMemo<FormAction[]>(() => ["GET"], []);

    const metadataSection = metadataAvailable ? (
        <Pivot>
            <PivotItem headerText={t("sql_tables")} itemKey="tables">
                <TenantList<
                    SqlTableMetadataDto,
                    SqlTableMetadataDto,
                    SqlTableMetadataDto
                >
                    id={`sql-tables-${identifier}`}
                    resource={TenantResource.SqlMetadataTables}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlTableMetadataDto>>}
                    getId={getSqlTableMetadataKey}
                    columns={tableColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleTableInvoked}
                    horizontal
                    allowSearching
                    title={"sql_tables"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                >
                    {(_, item) => renderObjectDetails(item ? {
                        objectType: "table",
                        schemaName: item.schemaName,
                        objectName: item.tableName,
                    } : undefined)}
                </TenantList>
            </PivotItem>
            <PivotItem headerText={t("sql_views")} itemKey="views">
                <TenantList<
                    SqlTableMetadataDto,
                    SqlTableMetadataDto,
                    SqlTableMetadataDto
                >
                    id={`sql-views-${identifier}`}
                    resource={TenantResource.SqlMetadataViews}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlTableMetadataDto>>}
                    getId={getSqlViewMetadataKey}
                    columns={viewColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleViewInvoked}
                    horizontal
                    allowSearching
                    title={"sql_views"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                >
                    {(_, item) => renderObjectDetails(item ? {
                        objectType: "view",
                        schemaName: item.schemaName,
                        objectName: item.tableName,
                    } : undefined)}
                </TenantList>
            </PivotItem>
            <PivotItem headerText={t("sql_procedures")} itemKey="procedures">
                <TenantList<
                    SqlRoutineMetadataDto,
                    SqlRoutineMetadataDto,
                    SqlRoutineMetadataDto
                >
                    id={`sql-procedures-${identifier}`}
                    resource={TenantResource.SqlMetadataProcedures}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlRoutineMetadataDto>>}
                    getId={getSqlProcedureMetadataKey}
                    columns={routineColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleProcedureInvoked}
                    horizontal
                    allowSearching
                    title={"sql_procedures"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                >
                    {(_, item) => renderObjectDetails(item ? {
                        objectType: "procedure",
                        schemaName: item.schemaName,
                        objectName: item.routineName,
                    } : undefined)}
                </TenantList>
            </PivotItem>
            <PivotItem headerText={t("sql_functions")} itemKey="functions">
                <TenantList<
                    SqlRoutineMetadataDto,
                    SqlRoutineMetadataDto,
                    SqlRoutineMetadataDto
                >
                    id={`sql-functions-${identifier}`}
                    resource={TenantResource.SqlMetadataFunctions}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlRoutineMetadataDto>>}
                    getId={getSqlFunctionMetadataKey}
                    columns={routineColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleFunctionInvoked}
                    horizontal
                    allowSearching
                    title={"sql_functions"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                >
                    {(_, item) => renderObjectDetails(item ? {
                        objectType: "function",
                        schemaName: item.schemaName,
                        objectName: item.routineName,
                    } : undefined)}
                </TenantList>
            </PivotItem>
            <PivotItem headerText={t("sql_users")} itemKey="users">
                <TenantList<SqlUserInfoDto, SqlUserInfoDto, SqlUserInfoDto>
                    id={`sql-users-${identifier}`}
                    resource={TenantResource.SqlMetadataUsers}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlUserInfoDto>>}
                    getId={getSqlUserMetadataKey}
                    columns={userColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleUserInvoked}
                    horizontal
                    allowSearching
                    title={"sql_users"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                >
                    {(_, item) => renderObjectDetails(item ? {
                        objectType: "user",
                        objectName: item.userName,
                    } : undefined)}
                </TenantList>
            </PivotItem>
            <PivotItem headerText={t("sql_triggers")} itemKey="triggers">
                <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                    id={`sql-triggers-${identifier}`}
                    resource={TenantResource.SqlMetadataTriggers}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                    getId={getSqlObjectMetadataKey}
                    columns={objectColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleObjectInvoked}
                    allowSearching
                    title={"sql_triggers"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                />
            </PivotItem>
            <PivotItem headerText={t("sql_other_objects")} itemKey="other-objects">
                <Pivot>
                    <PivotItem headerText={t("sql_synonyms")} itemKey="synonyms">
                        <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                            id={`sql-synonyms-${identifier}`}
                            resource={TenantResource.SqlMetadataSynonyms}
                            baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                            getId={getSqlObjectMetadataKey}
                            columns={objectColumns}
                            allowedActions={metadataReadOnlyActions}
                            itemInvoked={handleObjectInvoked}
                            allowSearching
                            title={"sql_synonyms"}
                            additionalIntParameter={metadataCompanyId}
                            additionalStringParameter={metadataConnectionString}
                            wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                        />
                    </PivotItem>
                    <PivotItem headerText={t("sql_sequences")} itemKey="sequences">
                        <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                            id={`sql-sequences-${identifier}`}
                            resource={TenantResource.SqlMetadataSequences}
                            baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                            getId={getSqlObjectMetadataKey}
                            columns={objectColumns}
                            allowedActions={metadataReadOnlyActions}
                            itemInvoked={handleObjectInvoked}
                            allowSearching
                            title={"sql_sequences"}
                            additionalIntParameter={metadataCompanyId}
                            additionalStringParameter={metadataConnectionString}
                            wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                        />
                    </PivotItem>
                    <PivotItem headerText={t("sql_types")} itemKey="types">
                        <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                            id={`sql-types-${identifier}`}
                            resource={TenantResource.SqlMetadataTypes}
                            baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                            getId={getSqlObjectMetadataKey}
                            columns={objectColumns}
                            allowedActions={metadataReadOnlyActions}
                            itemInvoked={handleObjectInvoked}
                            allowSearching
                            title={"sql_types"}
                            additionalIntParameter={metadataCompanyId}
                            additionalStringParameter={metadataConnectionString}
                            wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                        />
                    </PivotItem>
                    <PivotItem headerText={t("sql_schemas")} itemKey="schemas">
                        <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                            id={`sql-schemas-${identifier}`}
                            resource={TenantResource.SqlMetadataSchemas}
                            baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                            getId={getSqlObjectMetadataKey}
                            columns={objectColumns}
                            allowedActions={metadataReadOnlyActions}
                            itemInvoked={handleObjectInvoked}
                            allowSearching
                            title={"sql_schemas"}
                            additionalIntParameter={metadataCompanyId}
                            additionalStringParameter={metadataConnectionString}
                            wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                        />
                    </PivotItem>
                    <PivotItem headerText={t("sql_roles")} itemKey="roles">
                        <TenantList<SqlObjectMetadataDto, SqlObjectMetadataDto, SqlObjectMetadataDto>
                            id={`sql-roles-${identifier}`}
                            resource={TenantResource.SqlMetadataRoles}
                            baseDto={{} as TWithStoragesAndOriginals<Partial<SqlObjectMetadataDto>>}
                            getId={getSqlObjectMetadataKey}
                            columns={objectColumns}
                            allowedActions={metadataReadOnlyActions}
                            itemInvoked={handleObjectInvoked}
                            horizontal
                            allowSearching
                            title={"sql_roles"}
                            additionalIntParameter={metadataCompanyId}
                            additionalStringParameter={metadataConnectionString}
                            wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                        >
                            {(_, item) => renderObjectDetails(item ? {
                                objectType: "role",
                                objectName: item.objectName,
                            } : undefined)}
                        </TenantList>
                    </PivotItem>
                </Pivot>
            </PivotItem>
            <PivotItem headerText={t("sql_sessions")} itemKey="sessions">
                <TenantList<
                    SqlSessionInfoDto,
                    SqlSessionInfoDto,
                    SqlSessionInfoDto
                >
                    id={`sql-sessions-${identifier}`}
                    resource={TenantResource.SqlMetadataSessions}
                    baseDto={{} as TWithStoragesAndOriginals<Partial<SqlSessionInfoDto>>}
                    columns={sessionColumns}
                    allowedActions={metadataReadOnlyActions}
                    itemInvoked={handleSessionInvoked}
                    allowSearching
                    title={"sql_sessions"}
                    additionalIntParameter={metadataCompanyId}
                    additionalStringParameter={metadataConnectionString}
                    wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                />
            </PivotItem>
        </Pivot>
    ) : (
        <Text>
            {t("sql_metadata_connection_missing")}
        </Text>
    );

    const renderQueryResponse = useCallback((sqlQueryResponse: any[][] | undefined) => {
        return (
            <Pivot>
                <PivotItem title={t("Retci")} headerText={t("Retci")}>
                    {makeResultData(sqlQueryResponse)}
                </PivotItem>
                <PivotItem title={t("SQL oblik")} headerText={t("SQL oblik")}>
                    <CodeMirror
                        options={{
                            mode: "text/x-sql",
                            lineNumbers: true,
                        }}
                        className="big-codemirror"
                        onBeforeChange={() => {
                        }}
                        value={makeResponseText(sqlQueryResponse) ?? ""}
                    />
                </PivotItem>
                <PivotItem title={t("XML oblik")} headerText={t("XML oblik")}>
                    <CodeMirror
                        options={{
                            mode: "application/xml",
                            lineNumbers: true,
                        }}
                        className="big-codemirror"
                        onBeforeChange={() => {
                        }}
                        value={makeResponseText(sqlQueryResponse) ?? ""}
                    />
                </PivotItem>
            </Pivot>
        );
    }, [makeResultData, makeResponseText, t, inline]);

    return (
        <Stack tokens={{childrenGap: 10}} padding={10}>
            <Label>SQL Editor</Label>
            {mustCheck && (
                <h1 style={{color: SharedColors.red10}}>
                    OVAJ SQL UPIT IMA DELETE NAREDBU BEZ WHERE UVJETA, PROVJERITE DA LI TO
                    STVARNO ŽELITE POKRENUTI
                </h1>
            )}
            <CMEditor
                disabled={loadingLocal}
                endData={localSqlQuery ?? ""}
                language={"sql"}
                runClicked={async (value: string, selectedText?: string) => {
                    setLoadingTrue();
                    try {
                        await executeSqlQuery(undefined, value);
                        await sqlQueryChanged(undefined, value);
                    } finally {
                        setLoadingFalse();
                    }
                }}
                onChanged={(value: string) => {
                    setLocalSqlQuery(value);
                }}
                minHeight={defaultHeight ?? "50vh"}
                getSQLDefinitions={getSQLDefinitions}
                commands={additionalCommands}
            />
            {sqlQueryResponse ? (
                isMultiCompany && !Array.isArray(sqlQueryResponse) ? (
                    Object.keys(sqlQueryResponse).map((m) => (
                        <Stack key={m}>
                            <Label>{m}</Label>
                            {renderQueryResponse(sqlQueryResponse[m as any])}
                        </Stack>
                    ))
                ) : (
                    renderQueryResponse(sqlQueryResponse as any[][])
                )
            ) : (
                <Stack tokens={{childrenGap: 10}} style={{textAlign: "center"}}>
                    <Spinner/>
                    {!loading && (
                        <Text
                            style={{
                                color: sqlQueryError
                                    ? SharedColors.red10
                                    : sqlQueryWarning
                                        ? SharedColors.orangeYellow20
                                        : SharedColors.gray40,
                            }}
                        >
                            {sqlQueryError
                                ? t("fix_and_run_query") + ": " + t(sqlQueryError)
                                : sqlQueryWarning
                                    ? t("warning") + ": " + t(sqlQueryWarning)
                                    : t("start_query")}
                        </Text>
                    )}
                </Stack>
            )}
            <Pivot>
                <PivotItem headerText={t("sql_templates")} itemKey="templates">
                    <TenantList<SqlTemplateDto, SqlTemplateDto, SqlTemplateDto>
                        id={`sql-templates-${identifier}`}
                        resource={TenantResource.SQLTemplates}
                        baseDto={templateBaseDto}
                        form={templateForm}
                        columns={templateColumns}
                        allowSearching
                        title={"sql_templates"}
                        itemInvoked={handleTemplateApply}
                        additionalSelectionCommands={templateSelectionCommands}
                        wrapStyle={{maxHeight: "55vh", overflow: "auto"}}
                    />
                </PivotItem>
                <PivotItem headerText={t("sql_objects")} itemKey="objects">
                    {metadataSection}
                </PivotItem>
            </Pivot>
        </Stack>
    );
};
